import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { routes, formatBillingDate } from '../../../../particles/common';

const businessTypeChipConfig: Record<string, { label: string; backgroundColor: string }> = {
  estate: { label: 'Estate', backgroundColor: '#4CAF50' },
  tourism: { label: 'Tourism', backgroundColor: '#FF9800' },
  mortgage: { label: 'Mortgage', backgroundColor: '#9C27B0' },
  other: { label: 'Other', backgroundColor: '#607D8B' }
};

const generateRows = (
  tokens: BillingAccount[],
  onAction: (token: BillingAccount, value: unknown, type: 'edit' | 'delete' | 'pin' | 'history') => void
): TableRow[] =>
  tokens.map((token) => {
    const actions: TableCellAction['data'] = [
      {
        type: 'other',
        icon: token.isPinned ? PushPinIcon : PushPinOutlinedIcon,
        linkData: {
          onClick: () => {
            onAction(token, !token.isPinned, 'pin');
          }
        }
      },
      {
        // AI-model change log — opens a modal (see BillingAccount page).
        type: 'other',
        icon: HistoryOutlinedIcon,
        linkData: {
          onClick: () => {
            onAction(token, undefined, 'history');
          }
        }
      },
      {
        type: 'edit',
        linkData: {
          onClick: (value) => {
            onAction(token, value as boolean, 'edit');
          }
        }
      },
      {
        type: 'delete',
        linkData: {
          onClick: (value) => {
            onAction(token, value as boolean, 'delete');
          }
        }
      }
    ];

    const edit: TableCellAction = {
      type: `action`,
      data: actions
    };

    const twilio: TableCellChip = {
      type: 'chip',
      data: [
        {
          text: token.twilioSecret ? 'Yes' : 'No',
          backgroundColor: token.twilioSecret ? '#9ADE7B' : '#D32F33'
        }
      ]
    };

    let type: TableCellChip = {
      type: 'chip',
      data: [
        {
          text: 'C',
          backgroundColor: '#F5CB5C'
        }
      ]
    };
    if (token.locationId && token.reapitCompanyId) {
      type = {
        type: 'chip',
        data: [
          {
            text: 'N',
            backgroundColor: '#086BDE'
          },
          {
            text: 'C',
            backgroundColor: '#F5CB5C'
          }
        ]
      };
    } else if (token.locationId) {
      type = {
        type: 'chip',
        data: [
          {
            text: 'C',
            backgroundColor: '#F5CB5C'
          }
        ]
      };
    } else if (token.reapitCompanyId) {
      type = {
        type: 'chip',
        data: [
          {
            text: 'N',
            backgroundColor: '#086BDE'
          }
        ]
      };
    }

    const allocatedToken = token.totalToken ? token.totalToken : 0;
    const consumedToken = token.srtValuesBasedSubscription ? token.srtValuesBasedSubscription : 0;
    const rolling_month = token.rolling_month ? Number(token.rolling_month.toFixed(2)) : 0;
    const _availableToken = allocatedToken - consumedToken;

    const totalLeadsValue = token.totalLeads ? token.totalLeads : 0;
    // Overage cost + CPL are server-computed (see EntityGateway.ts
    // `overageCostExpr` / `costPerLeadExpr`). We consume them directly so the
    // sort column and the displayed value never drift.
    const usageCostValue = token.overageCost ?? 0;
    const costPerLeadValue = token.costPerLead ?? null;

    const availableToken: TableCellText = {
      data: {
        text: _availableToken.toFixed(2),
        fontColor: _availableToken < 0 ? '#D32F33' : 'rgba(0, 0, 0, 0.87)'
      },
      type: 'text'
    };

    const subscriptionDate = `${formatBillingDate(
      token.subscriptionDate
    )} - ${formatBillingDate(token.nextSubscriptionDate)}`;

    const btConfig = businessTypeChipConfig[token.businessType ?? ''] ?? { label: 'N/A', backgroundColor: '#BDBDBD' };
    const businessType: TableCellChip = {
      type: 'chip',
      data: [
        {
          text: btConfig.label,
          backgroundColor: btConfig.backgroundColor,
          size: 'small'
        }
      ]
    };

    const locationIdText = token.locationId ?? '';
    const locationIdCell: TableCellText = {
      type: 'text',
      data: { text: locationIdText, copyable: Boolean(locationIdText) }
    };

    const payload: TableRow = {
      locationId: locationIdCell,
      companyName: token.companyName ?? '',
      businessType,
      subscriptionDate,
      // twilioSecret: token.twilioSecret ? token.twilioSecret.substring(0, 3) + '**********' : '',
      // twilioToken: token.twilioToken ? token.twilioToken.substring(0, 3) + '**********' : '',
      twilioCredentials: twilio,
      totalToken: allocatedToken,
      rolling_month,
      availableToken,
      srtValuesBasedSubscription: consumedToken > 0 ? consumedToken.toFixed(2) : consumedToken,
      totalLeads: totalLeadsValue,
      costPerLead: costPerLeadValue !== null && totalLeadsValue > 0 ? `£${costPerLeadValue.toFixed(2)}` : '—',
      subscriptionCost: token.subscriptionCost ? `£${token.subscriptionCost.toFixed(2)}` : '£0.00',
      usageCost: {
        data: {
          text: `£${usageCostValue.toFixed(2)}`,
          fontColor: usageCostValue > 0 ? '#D32F33' : 'rgba(0, 0, 0, 0.87)'
        },
        type: 'text'
      } as TableCellText,
      type,
      priceModel: (token.priceModel),
      action: edit,
      linkData: {
        to: `${routes.SA_BillingTokens}/${token.id}`
      }
    };

    return payload;
  });

export default generateRows;
